import jsPDF from 'jspdf';
import { getApiBaseUrl } from '../../../utils/apiConfig';

const MARGIN = 12;
const MAX_EDGE = 1400;

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` };
}

function fitBox(pxW, pxH, maxW, maxH) {
  const natW = (pxW * 25.4) / 96;
  const natH = (pxH * 25.4) / 96;
  let w = Math.min(maxW, natW);
  let h = natW ? w * (natH / natW) : 0;
  if (h > maxH && h > 0) {
    h = maxH;
    w = h * (natW / natH);
  }
  return { w, h };
}

async function loadFigure(os, id) {
  const res = await fetch(
    `${getApiBaseUrl()}/api/board/orders/${encodeURIComponent(os)}/orya/figura/${id}`,
    { headers: authHeaders() },
  );
  if (!res.ok) return null;
  const bmp = await createImageBitmap(await res.blob());
  const scale = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bmp, 0, 0, w, h);
  const natural = { w: bmp.width, h: bmp.height };
  bmp.close?.();
  return { data: canvas.toDataURL('image/jpeg', 0.9), ...natural };
}

export async function downloadOryaAnswerPdf({ os, blocks, fontes }) {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const maxW = pageW - MARGIN * 2;
  const maxH = pageH - MARGIN * 2;
  let y = MARGIN;

  const nextPage = () => {
    pdf.addPage();
    y = MARGIN;
  };
  const room = (h) => {
    if (y > MARGIN && y + h > pageH - MARGIN) nextPage();
  };

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(100, 116, 139);
  pdf.text(`OS ${os}`, MARGIN, y);
  y += 6;

  for (const block of blocks) {
    if (typeof block === 'string') {
      const text = block.trim();
      if (!text) continue;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10.5);
      pdf.setTextColor(31, 33, 48);
      const lines = pdf.splitTextToSize(text, maxW);
      lines.forEach((line) => {
        room(4.6);
        pdf.text(line, MARGIN, y);
        y += 4.6;
      });
      y += 2;
      continue;
    }
    const figure = await loadFigure(os, block.id);
    if (!figure) {
      pdf.setFontSize(9);
      pdf.setTextColor(100, 116, 139);
      room(5);
      pdf.text('Figura indisponível', MARGIN, y);
      y += 6;
      continue;
    }
    const box = fitBox(figure.w, figure.h, maxW, maxH);
    room(box.h + 2);
    pdf.addImage(figure.data, 'JPEG', MARGIN, y, box.w, box.h);
    y += box.h + 1.5;
    const legenda = String(block.legenda || '').trim();
    if (legenda) {
      pdf.setFontSize(8);
      pdf.setTextColor(100, 116, 139);
      const caption = pdf.splitTextToSize(legenda, maxW);
      caption.forEach((line) => {
        room(3.6);
        pdf.text(line, MARGIN, y);
        y += 3.6;
      });
    }
    y += 2;
  }

  (fontes || []).forEach((fonte) => {
    const line = `${fonte.documento || 'Manual'}${fonte.pagina ? ` · p. ${fonte.pagina}` : ''}`;
    pdf.setFontSize(8);
    pdf.setTextColor(100, 116, 139);
    room(4);
    pdf.text(line, MARGIN, y);
    y += 4;
  });

  pdf.save(`Orya-OS-${os}.pdf`);
}
